import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Send, MessageSquare, Check, CheckCheck, X } from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonGrid } from './ui/PortalSkeleton'

type ChatSummary = {
  id: string
  status: 'pending' | 'accepted' | 'declined'
  last_message_at: string
  organizations: { name: string; logo_url: string | null } | null
}

type ChatMessage = {
  id: string
  sender_type: 'org' | 'journalist'
  content: string
  created_at: string
  delivered_at: string | null
  read_at: string | null
}

function MessageTicks({ deliveredAt, readAt }: { deliveredAt: string | null; readAt: string | null }) {
  if (readAt) return <CheckCheck className="w-3.5 h-3.5 text-sky-300" />
  if (deliveredAt) return <CheckCheck className="w-3.5 h-3.5 text-white/60" />
  return <Check className="w-3.5 h-3.5 text-white/60" />
}

export function PortalMessagesView() {
  const qc = useQueryClient()
  const [activeChatId, setActiveChatId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const { data: chats, isLoading } = useQuery({
    queryKey: ['portal-chats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data } = await (supabase
        .from('org_journalist_chats' as any)
        .select('id, status, last_message_at, organizations(name, logo_url)')
        .eq('journalist_id', user.id)
        .order('last_message_at', { ascending: false }) as any)
      return (data ?? []) as ChatSummary[]
    },
  })

  const requests = useMemo(() => (chats ?? []).filter(c => c.status === 'pending'), [chats])
  const conversations = useMemo(() => (chats ?? []).filter(c => c.status === 'accepted'), [chats])

  const activeChat = (chats ?? []).find(c => c.id === activeChatId) ?? conversations[0] ?? requests[0] ?? null
  const chatId = activeChat?.id ?? null

  const { data: messages = [] } = useQuery({
    queryKey: ['portal-chat-messages', chatId],
    enabled: !!chatId,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_journalist_chat_messages' as any)
        .select('id, sender_type, content, created_at, delivered_at, read_at')
        .eq('chat_id', chatId!)
        .order('created_at') as any)
      return (data ?? []) as ChatMessage[]
    },
  })

  useEffect(() => {
    if (!chatId) return
    supabase.from('org_journalist_chat_messages' as any)
      .update({ read_at: new Date().toISOString() })
      .eq('chat_id', chatId).eq('sender_type', 'org').is('read_at', null)
      .then(() => qc.invalidateQueries({ queryKey: ['portal-chat-messages', chatId] }))
  }, [chatId, messages.length])

  const respond = useMutation({
    mutationFn: async (status: 'accepted' | 'declined') => {
      if (!chatId) return
      const { error } = await (supabase.from('org_journalist_chats' as any).update({ status }).eq('id', chatId) as any)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-chats'] }),
  })

  const send = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!chatId || !user || !draft.trim()) return
      const { error } = await (supabase
        .from('org_journalist_chat_messages' as any)
        .insert({ chat_id: chatId, sender_type: 'journalist', sender_id: user.id, content: draft.trim() }) as any)
      if (error) throw error
      // Replying to a message request accepts it, Instagram-style.
      if (activeChat?.status === 'pending') {
        await (supabase.from('org_journalist_chats' as any).update({ status: 'accepted' }).eq('id', chatId) as any)
      }
    },
    onSuccess: () => {
      setDraft('')
      qc.invalidateQueries({ queryKey: ['portal-chat-messages', chatId] })
      qc.invalidateQueries({ queryKey: ['portal-chats'] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PortalPageHeader title="Messages" description="Conversations with institutions." />
        <PortalSkeletonGrid cards={3} />
      </div>
    )
  }

  if (!chats || chats.length === 0) {
    return (
      <div className="space-y-6">
        <PortalPageHeader title="Messages" description="Conversations with institutions." />
        <PortalEmptyState icon={MessageSquare} message="No conversations yet. Institutions you haven't applied to can reach out to you here." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PortalPageHeader title="Messages" description="Conversations with institutions." />
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 border rounded-xl overflow-hidden" style={{ minHeight: 420 }}>
        <div className="border-r divide-y overflow-y-auto">
          {requests.length > 0 && (
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50">
              Requests ({requests.length})
            </div>
          )}
          {requests.map(c => (
            <ChatListItem key={c.id} chat={c} active={chatId === c.id} onClick={() => setActiveChatId(c.id)} badge="New" />
          ))}
          {conversations.length > 0 && (
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50">
              Messages
            </div>
          )}
          {conversations.map(c => (
            <ChatListItem key={c.id} chat={c} active={chatId === c.id} onClick={() => setActiveChatId(c.id)} />
          ))}
        </div>
        <div className="flex flex-col p-4">
          {activeChat?.status === 'pending' && (
            <div className="flex items-center justify-between gap-2 bg-muted rounded-lg px-3 py-2 mb-3">
              <p className="text-xs text-muted-foreground">
                {activeChat.organizations?.name ?? 'This institution'} wants to message you. Accept to start the conversation, or reply to accept automatically.
              </p>
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => respond.mutate('declined')}>
                  <X className="w-3.5 h-3.5" /> Decline
                </Button>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={() => respond.mutate('accepted')}>
                  <Check className="w-3.5 h-3.5" /> Accept
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No messages yet.</p>}
            {messages.map(m => (
              <div key={m.id} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender_type === 'journalist' ? 'ml-auto bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <div>{m.content}</div>
                {m.sender_type === 'journalist' && (
                  <div className="flex justify-end mt-0.5">
                    <MessageTicks deliveredAt={m.delivered_at} readAt={m.read_at} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-3 border-t mt-3">
            <Textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="Write a message..." className="min-h-9 resize-none text-sm" rows={1} />
            <Button size="icon" disabled={!draft.trim() || send.isPending || activeChat?.status === 'declined'} onClick={() => send.mutate()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ChatListItem({ chat, active, onClick, badge }: { chat: ChatSummary; active: boolean; onClick: () => void; badge?: string }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 hover:bg-muted ${active ? 'bg-muted' : ''}`}
    >
      {chat.organizations?.logo_url ? (
        <img src={chat.organizations.logo_url} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
      ) : (
        <div className="w-6 h-6 rounded bg-primary/10 shrink-0" />
      )}
      <span className="truncate flex-1">{chat.organizations?.name ?? 'Institution'}</span>
      {badge && <Badge className="text-[10px] h-4 px-1.5 shrink-0">{badge}</Badge>}
    </button>
  )
}
